import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Instagram, Facebook, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import { BRAND } from "../lib/site-data";

const HOURS = [
  { day: "Mon – Fri", hrs: "8:00 AM – 6:00 PM" },
  { day: "Saturday", hrs: "9:00 AM – 3:00 PM" },
  { day: "Sunday", hrs: "By appointment" },
];

export default function ContactPage() {
  return (
    <main data-testid="contact-page">
      <PageHeader
        eyebrow="Get in touch"
        title="Contact."
        accent="We'd love to hear from you."
        subtitle="Call, email, or send us a quote request. We respond within one business day — always."
      />

      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
            {/* Left — details */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-7 space-y-10"
              data-testid="contact-info"
            >
              {/* Phones */}
              <div>
                <div className="text-[11px] uppercase tracking-[0.28em] text-slate-500 mb-5">
                  Call us
                </div>
                <div className="grid sm:grid-cols-1 gap-4">
                  {BRAND.phones.map((p) => (
                    <a
                      key={p.tel}
                      href={`tel:${p.tel}`}
                      className="group relative block rounded-lg border border-white/5 bg-[#2a3744] p-5 hover:border-[#D4AF37]/40 transition-colors"
                      data-testid={`contact-phone-${p.name.toLowerCase()}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-[#1f2935] border border-white/10 flex items-center justify-center">
                          <Phone
                            size={15}
                            strokeWidth={1.5}
                            className="text-[#D4AF37]"
                          />
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
                            {p.name}
                          </div>
                          <div className="mt-0.5 font-serif text-xl text-white group-hover:text-[#D4AF37] transition-colors">
                            {p.value}
                          </div>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>

              {/* Email + Location */}
              <div className="grid sm:grid-cols-2 gap-4">
                <a
                  href={`mailto:${BRAND.email}`}
                  className="group block rounded-lg border border-white/5 bg-[#2a3744] p-5 hover:border-[#D4AF37]/40 transition-colors"
                  data-testid="contact-email"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-[#1f2935] border border-white/10 flex items-center justify-center">
                      <Mail
                        size={14}
                        strokeWidth={1.5}
                        className="text-[#D4AF37]"
                      />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
                        Email
                      </div>
                      <div className="mt-0.5 text-[15px] text-white group-hover:text-[#D4AF37] transition-colors break-all">
                        {BRAND.email}
                      </div>
                    </div>
                  </div>
                </a>

                <div className="rounded-lg border border-white/5 bg-[#2a3744] p-5">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-[#1f2935] border border-white/10 flex items-center justify-center">
                      <MapPin
                        size={14}
                        strokeWidth={1.5}
                        className="text-[#D4AF37]"
                      />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
                        Service area
                      </div>
                      <div className="mt-0.5 text-[15px] text-white">
                        {BRAND.serviceArea}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Socials */}
              <div className="flex items-center gap-4 pt-2">
                <span className="text-[11px] uppercase tracking-[0.28em] text-slate-500">
                  Follow
                </span>
                <a
                  href={BRAND.socials.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="h-9 w-9 rounded-full border border-white/10 flex items-center justify-center text-slate-400 hover:text-[#D4AF37] hover:border-[#D4AF37]/40 transition-colors"
                  aria-label="Instagram"
                  data-testid="social-instagram"
                >
                  <Instagram size={15} strokeWidth={1.5} />
                </a>
                <a
                  href={BRAND.socials.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="h-9 w-9 rounded-full border border-white/10 flex items-center justify-center text-slate-400 hover:text-[#D4AF37] hover:border-[#D4AF37]/40 transition-colors"
                  aria-label="Facebook"
                  data-testid="social-facebook"
                >
                  <Facebook size={15} strokeWidth={1.5} />
                </a>
              </div>
            </motion.div>

            {/* Right — hours + CTA */}
            <motion.aside
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="lg:col-span-5 space-y-6"
            >
              <div className="rounded-lg border border-white/5 bg-[#2a3744] p-6 sm:p-7">
                <div className="flex items-center gap-3 mb-5">
                  <Clock
                    size={14}
                    strokeWidth={1.5}
                    className="text-[#D4AF37]"
                  />
                  <span className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37]">
                    Hours
                  </span>
                </div>
                <ul className="space-y-3 divide-y divide-white/5">
                  {HOURS.map((h) => (
                    <li
                      key={h.day}
                      className="flex justify-between pt-3 first:pt-0 text-[14px]"
                    >
                      <span className="text-white">{h.day}</span>
                      <span className="text-slate-400">{h.hrs}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-lg border border-[#D4AF37]/30 bg-gradient-to-br from-[#D4AF37]/10 to-transparent p-6 sm:p-7">
                <h3 className="font-serif text-xl text-white leading-tight">
                  Prefer to skip the phone tag?
                </h3>
                <p className="mt-2 text-[13px] text-slate-300 leading-relaxed">
                  Send a quote request — we'll reply with an estimate the same
                  day.
                </p>
                <Link
                  to="/quote"
                  className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#D4AF37] text-[#1f2935] font-semibold text-[11px] uppercase tracking-[0.22em] hover:bg-[#B48600] transition-colors"
                  data-testid="contact-cta-quote"
                >
                  Get a Quote
                </Link>
              </div>
            </motion.aside>
          </div>
        </div>
      </section>
    </main>
  );
}
